// src/components/EmailVerificationPending.js
import React, { useState } from "react";
import {
  Paper,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Fade,
  CircularProgress,
} from "@mui/material";
import {
  MarkEmailRead,
  Refresh,
  Logout,
  CheckCircle,
} from "@mui/icons-material";
import { sendEmailVerification, signOut } from "firebase/auth";
import { auth, database } from "../firebase/Config";
import { ref, update } from "firebase/database";
import { useNavigate } from 'react-router-dom';

const EmailVerificationPending = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleResendEmail = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const user = auth.currentUser;
      
      if (!user) {
        setMessage({ 
          text: "Nenhum usuário conectado. Faça login novamente.", 
          type: "error" 
        });
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      await sendEmailVerification(user);
      setEmailSent(true);
      setMessage({ 
        text: "Email de verificação reenviado com sucesso! Verifique sua caixa de entrada.", 
        type: "success" 
      });
    } catch (err) {
      console.error("Erro ao reenviar email:", err);
      
      let errorMessage = "Erro ao reenviar email. Tente novamente.";
      
      if (err.code === 'auth/too-many-requests') {
        errorMessage = "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = "Erro de conexão. Verifique sua internet.";
      }
      
      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error("Erro ao fazer logout:", err);
      setMessage({ text: "Erro ao sair. Tente novamente.", type: "error" });
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    
    try {
      const user = auth.currentUser;
      
      if (!user) {
        setMessage({ 
          text: "Nenhum usuário conectado. Faça login novamente.", 
          type: "error" 
        });
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Recarregar os dados do usuário para verificar o status de verificação
      await user.reload();
      
      if (user.emailVerified) {
        // Atualizar o status no Realtime Database
        try {
          await update(ref(database, `users/${user.uid}`), {
            emailVerified: true,
          });
        } catch (dbError) {
          console.error("Erro ao atualizar database:", dbError);
          // Continua mesmo se o database falhar
        }

        setMessage({ 
          text: "Email verificado com sucesso! Redirecionando...", 
          type: "success" 
        });
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setMessage({ 
          text: "Email ainda não verificado. Verifique sua caixa de entrada e clique no link.", 
          type: "warning" 
        });
      }
    } catch (err) {
      console.error("Erro ao verificar email:", err);
      setMessage({ text: "Erro ao verificar status. Tente novamente.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setMessage({ text: "", type: "" });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
      }}
    >
      <Fade in={true} timeout={800}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: 500,
            borderRadius: 4,
            background: '#ffffff',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center',
          }}
        >
          {/* Ícone e Título */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 8px 24px rgba(72, 187, 120, 0.4)',
              }}
            >
              <MarkEmailRead sx={{ fontSize: 56, color: '#fff' }} />
            </Box>
            
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#2d3748',
                mb: 2,
              }}
            >
              Verifique seu Email
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enviamos um link de verificação para o seu email.
              <br />
              Por favor, clique no link para ativar sua conta.
            </Typography>

            {emailSent && (
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                Email de verificação reenviado com sucesso!
              </Alert>
            )}

            <Box
              sx={{
                bgcolor: '#fff3cd',
                borderRadius: 2,
                p: 2.5,
                mb: 3,
                border: '1px solid #ffc107',
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Instruções Importantes:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ textAlign: 'left' }}>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Verifique sua caixa de spam ou lixo eletrônico</strong> - o email de confirmação pode ter sido direcionado para lá
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Certifique-se de que o email cadastrado é válido</strong> e está correto
                  </li>
                  <li>
                    O link de verificação expira em <strong>24 horas</strong>
                  </li>
                </ul>
              </Typography>
            </Box>
          </Box>

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              onClick={handleCheckVerification}
              sx={{
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                transition: 'all 0.3s',
                '&:hover': {
                  background: 'linear-gradient(135deg, #38a169 0%, #48bb78 100%)',
                  boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: '#cccccc',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? 'Verificando...' : 'Já Verifiquei meu Email'}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              disabled={loading}
              startIcon={<Refresh />}
              onClick={handleResendEmail}
              sx={{
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#48bb78',
                color: '#48bb78',
                '&:hover': {
                  borderColor: '#38a169',
                  bgcolor: '#f7fafc',
                },
                '&:disabled': {
                  borderColor: '#cccccc',
                  color: '#cccccc',
                },
              }}
            >
              Reenviar Email de Verificação
            </Button>

            <Button
              variant="text"
              fullWidth
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{
                py: 1.5,
                fontSize: 14,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none',
                color: '#718096',
                '&:hover': {
                  bgcolor: '#f7fafc',
                  color: '#2d3748',
                },
              }}
            >
              Voltar ao Login
            </Button>
          </Box>

          {/* Informações Adicionais */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary">
              Problemas com a verificação?
              <br />
              Entre em contato com o suporte.
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Snackbar para Mensagens */}
      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={message.type}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>

      {/* Copyright */}
      <Typography
        variant="body2"
        sx={{
          position: 'fixed',
          bottom: 16,
          width: '100%',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.9)',
          fontWeight: 500,
        }}
      >
        © 2025 AAP-GIS. Todos os direitos reservados.
      </Typography>
    </Box>
  );
};

export default EmailVerificationPending;
